/**
 * Public type contract for the resumable-upload client.
 *
 * The layering this file encodes is what makes the protocol testable:
 *
 * - The **driver** ({@link ChunkTransport} consumer, `resumable.ts`) speaks only
 *   {@link ChunkRequest} / {@link ChunkResponse}. It never constructs an
 *   `XMLHttpRequest`, so a fake transport that returns literals exercises every
 *   branch of the protocol without a network.
 * - The **transport** ({@link ChunkTransport}, `xhrTransport.ts`) is the one
 *   place that knows XHR exists. It classifies every raw HTTP outcome into a
 *   {@link ChunkResponse} and rejects for exactly one reason: the signal
 *   aborted.
 * - The **task** (`reflectionUploadTask.ts`, whose run is `performUpload.ts`)
 *   brackets the driver with the two tRPC mutations and owns
 *   {@link UploadStatus}, progress and cancellation. `useResumableUpload.ts` is
 *   the React view over it.
 *
 * None of these types imports a cloud SDK or tRPC; the port-interface
 * discipline (root CLAUDE.md) holds at the file boundary. The shapes are
 * narrower than the router's on purpose, and `useResumableUpload` binds the
 * real tRPC client to {@link UploadApi}, so a router change that breaks them
 * fails typecheck there.
 */
import type {
  ReflectionProcessingStatus,
  UploadContentType,
  UploadFailureKind,
  UploadTransportReason,
  WelcomeEntryProps,
} from "@reflect/domain";
import type { ErrorMessageKey, RecordMessageKey } from "@reflect/i18n";

import type { TrackFn } from "../analytics/track";

/**
 * Catalog keys in the `web.record.error.*` namespace the record flow surfaces.
 *
 * Derived from `Messages["web"]["record"]["error"]` in `@reflect/i18n`
 * (mirroring {@link ErrorMessageKey}), so a key the client emits with no copy
 * behind it is a type error here rather than a runtime miss. The upload emits
 * `tooShort`, `unsupportedFormat` and `sessionGone`; the recorder emits
 * `unsupportedBrowser` and `noChunks`.
 */
export type { RecordMessageKey };

/**
 * The reason an upload's `AbortController` is aborted.
 *
 * Cancellation is exclusively an `AbortSignal`. Every deliberate cancellation
 * (`abort()`, a reset, a superseding run, an abandoned stream) passes an
 * explicit instance of this error, so the signal's `reason` is a stable,
 * identifiable object: it is what lets the run tell a cancellation from a
 * genuine failure. The DOM would supply an `AbortError` `DOMException` even for
 * a bare `abort()`, so this exists for identity and diagnosability, not to
 * avoid an `undefined` reason.
 */
export class UploadAbortedError extends Error {
  constructor(message = "Upload aborted") {
    super(message);
    this.name = "UploadAbortedError";
  }
}

/** One request the driver makes against the resumable session URI. */
export interface ChunkRequest {
  /** The session URI minted by `upload.createResumableSession`. */
  url: string;
  /** Chunk bytes, or `null` for an offset probe. */
  body: Blob | null;
  /** An inclusive `bytes {s}-{e}/{total}` for a chunk, or the star probe form. */
  contentRange: string;
  /** Per-request ceiling; a half-open socket must fail, not hang. */
  timeoutMs: number;
  /**
   * How long the request may go without upload progress before it is
   * abandoned as a `stall`. Optional so a caller that does not
   * care gets the central default (`UPLOAD_CHUNK_STALL_TIMEOUT_MS`), applied by
   * the transport. It is never "off".
   */
  stallTimeoutMs?: number;
  /** The ONLY cancellation channel. */
  signal: AbortSignal;
  /** Bytes of THIS request's body flushed to the network so far. */
  onProgress?: (loaded: number) => void;
}

/**
 * The protocol-level outcome of a {@link ChunkRequest}.
 *
 * The transport resolves with one of these for every HTTP outcome and rejects
 * only on abort, which keeps cancellation out of the classification switch.
 */
export type ChunkResponse =
  | { kind: "complete" }
  | { kind: "incomplete"; nextOffset: number }
  /**
   * A `308` whose `Range` response header could not be read.
   *
   * Two different states produce this and **JavaScript cannot tell them
   * apart**, because `getResponseHeader` answers `null` for both: the server
   * genuinely holds zero bytes (GCS omits `Range` at offset 0), or the header
   * was sent and the browser withheld it because the response's
   * `Access-Control-Expose-Headers` did not list it. Which of the two it is
   * cannot be resolved at this layer, so the ambiguity is what gets reported.
   *
   * The bucket's own policy governs the second case:
   * `MEDIA_UPLOAD_CORS_EXPOSED_HEADERS` (`packages/domain/src/config/storage.ts`)
   * carries `range`, and stage 35 writes it into the single GCS `responseHeader`
   * field that drives both preflight and expose. So on a converged bucket the
   * header should be readable — which is exactly why a `null` here is worth a
   * distinct member instead of a guess.
   *
   * It is therefore **not** `{ kind: "incomplete", nextOffset: 0 }`. Reading it
   * as "the server holds nothing" throws away a committed offset the client has
   * already been told about, and re-sends from byte 0 (or, once past the first
   * chunk, trips the driver's offset-regressed guard and ends the run).
   */
  | { kind: "offset-unknown" }
  | { kind: "session-gone"; status: 404 | 410 }
  | { kind: "client-error"; status: number }
  | { kind: "protocol-error"; detail: string }
  | { kind: "retryable"; reason: UploadTransportReason; status?: number };

/**
 * The transport seam: one function from a {@link ChunkRequest} to its
 * {@link ChunkResponse}. `xhrTransport` is the default binding; tests inject a
 * fake so the driver runs with no network.
 */
export type ChunkTransport = (req: ChunkRequest) => Promise<ChunkResponse>;

export type { UploadFailureKind };

/** The observable state of one upload, rendered by the UI. */
export type UploadStatus =
  | { phase: "idle" }
  | { phase: "creating"; attempt: number }
  | { phase: "uploading"; bytesCommitted: number; totalBytes: number; attempt: number }
  | { phase: "resuming"; bytesCommitted: number; totalBytes: number; attempt: number }
  | { phase: "finalizing"; totalBytes: number; attempt: number }
  | {
      phase: "succeeded";
      reflectionId: string;
      processingStatus: ReflectionProcessingStatus;
      /** Wall-clock ms from `start()`/`retry()` to success, backoffs included; never rendered. */
      durationMs?: number;
      /** Chunk PUTs and probes that came back retryable (and were retried); never rendered. */
      retryCount?: number;
      /** Of those, the ones that hung (`stall` or `timeout`) rather than failed; never rendered. */
      stallCount?: number;
    }
  | {
      phase: "failed";
      messageKey: ErrorMessageKey | RecordMessageKey;
      requestId: string | null;
      canRetry: boolean;
      /** Developer-facing detail for protocol failures; never rendered. */
      detail?: string;
      /** Machine-readable cause for telemetry; never rendered. */
      failureKind?: UploadFailureKind;
      /** Server-acknowledged bytes when the run gave up; never rendered. */
      bytesCommitted?: number;
      /** The recording's byte length, once pre-flight has measured it; never rendered. */
      totalBytes?: number;
      /** The pending object key, once a session was minted; never rendered. */
      objectKey?: string;
      /**
       * Consecutive attempts burned on the operation that gave up; never
       * rendered. Resets whenever the upload makes progress, so it is not the
       * run's retry count. That is {@link retryCount}.
       */
      attempt?: number;
      /**
       * Chunk PUTs and probes that came back retryable over the whole run,
       * including the one that used up the budget; never rendered. The same
       * count `succeeded` carries, so `upload_failed` and `upload_succeeded`
       * report one quantity.
       */
      retryCount?: number;
      /**
       * The last transport verdict behind an `exhausted` run; never rendered.
       *
       * `network` is the one that names an outage: `XMLHttpRequest` reports a
       * CORS-blocked or DNS-failed request as `onerror` with `status === 0`, so
       * without this the request that never left the browser is indistinguishable
       * from five 503s and a flaky train tunnel.
       */
      lastTransportReason?: UploadTransportReason;
      /** HTTP status of that last verdict, when there was one; never rendered. */
      lastHttpStatus?: number;
      /** Wall-clock ms from `start()`/`retry()` to the failure, backoffs included; never rendered. */
      durationMs?: number;
      /** Of {@link retryCount}, the ones that hung (`stall` or `timeout`); never rendered. */
      stallCount?: number;
    }
  | { phase: "aborted" };

/**
 * The phases in which an upload is actually moving — bytes are on the wire, or
 * a mutation the run needs is outstanding.
 *
 * The complement is every phase with nothing left to wait for: `idle` (not
 * begun), `succeeded`, `failed` and `aborted`. That distinction is not the
 * same as "the upload screen is showing", because the recorder renders a
 * failure inside the upload screen's own frame — so anything that counts down
 * against a running upload (the handover clocks, the stall watchdog) must ask
 * this, not the view.
 */
export const UPLOAD_IN_FLIGHT_PHASES: ReadonlySet<UploadStatus["phase"]> = new Set([
  "creating",
  "uploading",
  "resuming",
  "finalizing",
] as const satisfies readonly UploadStatus["phase"][]);

/** Input `upload.createResumableSession` accepts (`apps/api/src/routers/upload.ts`). */
export interface CreateResumableSessionInput {
  /** MIME type the client will upload; must be allowlisted. */
  contentType: UploadContentType;
  /**
   * Exact byte length the client declares up front, or absent for a take
   * streamed while it records, whose length is not known yet.
   */
  sizeBytes?: number;
}

/**
 * The subset of `upload.createResumableSession`'s result the client acts on.
 *
 * The server also returns an advisory `expiresAt`; the client persists nothing,
 * so the field is not modelled here. The real result is structurally wider,
 * which is assignable to this narrower shape.
 */
export interface CreateResumableSessionResult {
  /** The resumable session URI the chunk loop PUTs against. */
  uploadUrl: string;
  /** The pending object key `finalize` is later called with. */
  key: string;
  /** The server's resolved byte cap, echoed for the client's pre-check. */
  maxBytes: number;
}

/** Input `upload.finalize` accepts. */
export interface FinalizeInput {
  /** The pending object key returned by `createResumableSession`. */
  key: string;
  /**
   * Client-declared recording length; advisory, since the media pipeline
   * measures the real one.
   */
  durationSeconds?: number;
  /** Optional user-supplied title. */
  title?: string;
  /** The welcome handoff the recording came from, reported on `entry_saved_success`. */
  welcome?: WelcomeEntryProps;
}

/** What `upload.finalize` returns, on both the fresh and idempotent paths. */
export interface FinalizeResult {
  /** The reflection the upload now belongs to. */
  reflectionId: string;
  /** The row's persisted media-pipeline status, read back from the row. */
  processingStatus: ReflectionProcessingStatus;
  /**
   * `true` only on the finalize that committed the account's first-ever
   * reflection; every idempotent retry answers `false` (card #1062).
   */
  firstReflection: boolean;
}

/**
 * The two tRPC mutations the upload calls, narrowed to a plain-promise interface.
 *
 * Injecting this (rather than reaching for the tRPC client inside the protocol
 * layer) is what lets the tests drive an upload with a fake client and no
 * `TRPCProvider`. In production `useResumableUpload` builds one from the tRPC
 * context.
 */
export interface UploadApi {
  /** Mint a fresh resumable session for one recording. Safe to retry. */
  createResumableSession(input: CreateResumableSessionInput): Promise<CreateResumableSessionResult>;
  /** Turn a committed upload into a reflection. Idempotent. */
  finalize(input: FinalizeInput): Promise<FinalizeResult>;
}

/** Injected sleep so backoff is instant in tests and abortable in production. */
export type SleepFn = (ms: number, signal: AbortSignal) => Promise<void>;

/** Injected wall clock (epoch ms) so an upload's measured duration is deterministic in tests. */
export type NowFn = () => number;

/** Options for {@link useResumableUpload}. */
export interface UseResumableUploadOptions {
  /**
   * The caller's authoritative byte cap, from `me.entitlements`. **Required.**
   * The record page does not render the recorder until the query resolves, so
   * this is always a number at the call site.
   */
  maxUploadBytes: number;
  /** Override for tests and the local-only e2e `?chunkBytes=` seam. */
  chunkSizeBytes?: number;
  /** Injected in tests; defaults to `xhrTransport`. */
  transport?: ChunkTransport;
  /** Injected clock for backoff, so tests never sleep. */
  sleep?: SleepFn;
  /**
   * Injected wall clock for the run's `durationMs`. Defaults to `Date.now`:
   * wall-clock rather than monotonic on purpose, because the number answers
   * "how long did the person wait", and time the phone spent with the tab in
   * the background is part of that.
   */
  now?: NowFn;
  /**
   * Injected tRPC caller for tests. Defaults to one built from the tRPC
   * context. A wiring/test seam in the same category
   * as {@link UseResumableUploadOptions.transport} and
   * {@link UseResumableUploadOptions.sleep}; consumers never pass it.
   */
  uploadApi?: UploadApi;
  /**
   * Injected analytics sink; defaults to the real one.
   *
   * Passed down to the task because the two terminal upload events are emitted
   * there: the run outlives the recorder, and for a client-side upload failure
   * those events are the only trace that exists anywhere.
   */
  track?: TrackFn;
}

/** Extra metadata `start()` / `retry()` carry into `finalize`. */
export interface StartOptions {
  /** Recording length, forwarded to `finalize` (advisory server-side). */
  durationSeconds?: number;
  /** Optional user-supplied title, forwarded to `finalize`. */
  title?: string;
  /** The welcome handoff, forwarded to `finalize`. */
  welcome?: WelcomeEntryProps;
}

/** The surface {@link useResumableUpload} returns to a component. */
export interface UseResumableUploadResult {
  /** The current upload state. */
  status: UploadStatus;
  /** 0..1, monotonic — the display progress the bar renders. */
  progress: number;
  /** Begin uploading `blob`. Idempotent within one recording via the once-guard. */
  start(blob: Blob, opts?: StartOptions): void;
  /** Re-upload the same blob under a FRESH session. */
  retry(): void;
  /** Cancel any in-flight upload. */
  abort(): void;
  /** Clear the once-guard and release the blob for the NEXT recording. */
  reset(): void;
  /**
   * Begin streaming a take as it records; call when the recorder
   * starts, with its MIME type. Best-effort: a no-op when streaming does not
   * apply, and never the cause of a failed upload.
   */
  beginLive(mimeType: string): void;
  /** Feed the take being streamed one recorder chunk, in assembly order. */
  appendLive(chunk: Blob): void;
  /** Drop the take being streamed; it was thrown away rather than finished. */
  abandonLive(): void;
  /**
   * When the run in flight began, epoch ms, or `null` while idle.
   *
   * Exposed because the record screen's handover offer is a judgement about how
   * long the **upload** has been running, and a component's own mount clock is
   * not the same thing: the run outlives the screen, so a reader who arrives at
   * a second screen mid-upload would otherwise restart the count and be offered
   * a way out of a wait that is already half over.
   */
  startedAt: number | null;
  /**
   * When the upload last got measurably closer to done, epoch ms, or `null`
   * while idle: the mark a hard stall is measured from.
   */
  lastProgressAt: number | null;
}
